/**
 * COMPTABILITÉ — RÈGLES & AUTOMATISATIONS.
 *
 * ═══ LA SAISON NE SE DEVINE PAS ═══
 *
 * L'exercice commercial n'est PAS l'année civile, et il n'était configuré nulle
 * part. Tant que personne ne l'a fixé, cet écran affiche « Saison à configurer »
 * — il n'invente aucune date de début. Choisir septembre parce que c'est
 * courant dans la nuit aurait produit des cumuls faux qui auraient l'air justes,
 * et que personne n'aurait validés.
 *
 * ═══ AUCUN SECRET ICI ═══
 *
 * Les jetons, mots de passe et identifiants fournisseur vivent dans
 * Administration → Intégrations, et nulle part ailleurs. Cet écran peut dire
 * QU'UNE SOURCE VA BIEN ; il n'affiche jamais ce qui permet de s'y connecter.
 *
 * ═══ CE QU'UN RÉGLAGE CHANGE VRAIMENT ═══
 *
 * Remonter discrètement un seuil d'écart de 10 € à 500 € fait disparaître des
 * alertes sans rien casser : c'est exactement le genre de changement qui doit
 * laisser un nom et une date. Chaque écriture passe donc par une mutation
 * tracée, avec ré-authentification forte.
 *
 * ROUTES (§8) :
 *   GET /api/v1/finance/settings   finance.reference.read
 *   PUT /api/v1/finance/settings   finance.settings.manage (step-up)
 *   GET /api/v1/finance/sources    finance.source.read (facultatif, état seul)
 */

import { useEffect, useMemo, useState } from 'react'
import { CalendarCheck, CalendarDays, Coins, Info, Scale, ShieldCheck, Ticket } from 'lucide-react'
import { Link } from '../router.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Select, adminMutate, apiGet, usePermissions } from '../admin/primitives.js'
import { Field, SwitchRow, TextInput } from '../admin/form.js'
import { useSaveBar } from '../shell/editing.js'
import { formatMoney, parseMoney } from './money.js'
import { EtatBloc, entier, estObjet, libelleJour, libelleJourLong, texte, useRessource } from './RevenueShared.js'
import { PartAssociation, lireParts, type EvenementPart, type PartsAssociations } from './PartAssociation.js'
import './finance.css'
import './finance-revenue.css'

/* ═══════════════════════════ les réglages ═══════════════════════════ */

interface Reglages {
  moisDebut: number | null
  jourDebut: number | null
  orphelineAuto: boolean
  coupures: number[]
  seuilSignalement: number | null
  seuilAlerte: number | null
  /** L'exercice EN COURS, calculé par le serveur. `null` = non configuré. */
  saisonCourante: { debut: string; fin: string } | null
  /** Les nuits : première nuit suivie, délais (heures après la fin de nuit), récapitulatif du mardi. */
  suiviDepuis: string
  graceZapHeures: number
  delaiClotureHeures: number
  recapMardi: boolean
  /** Au-delà de cette baisse, finaliser une nuit validée en bloc exige un motif (16/10/2026). */
  seuilRemplacement: number
  /** La billetterie des associations (Pretix), 16/10/2026 — `billetterie-associations.ts`. */
  assoEntrees: boolean
  assoDansLeChiffre: boolean
  assoPartDefaut: number
  assoFrais: boolean
  assoFraisBp: number
  assoFraisFixe: number
}

/** « 1,5 » (%) → 150 points de base ; `null` hors de 0 à 10 %. */
function lirePourcentBp(t: string): number | null {
  const x = t.trim().replace(',', '.')
  if (!/^\d{1,2}(\.\d{1,2})?$/.test(x)) return null
  const bp = Math.round(Number(x) * 100)
  return bp >= 0 && bp <= 1000 ? bp : null
}
const pourcent = (bp: number): string => String(bp / 100).replace('.', ',')
const euros = (c: number): string => String(c / 100).replace('.', ',')

/**
 * Coupures françaises en circulation. Le billet de 500 € n'y est pas :
 * l'émission a cessé en 2019 et une caisse de club n'en voit jamais. Une ligne
 * de comptage toujours vide est une ligne qu'on finit par cocher sans lire.
 */
const COUPURES = [20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5, 2, 1]

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']

function lireReglages(v: unknown): Reglages | null {
  if (!estObjet(v)) return null
  const o = estObjet(v['settings']) ? v['settings'] : v
  // Un objet qui ne porte AUCUNE clé attendue n'est pas un réglage vide :
  // c'est une réponse qui ne parle pas de ça. On refuse plutôt que d'afficher
  // des défauts inventés dans un formulaire qui prétendrait les avoir lus.
  const attendues = ['season_start_month', 'orphan_auto_create', 'denominations_cents', 'discrepancy_warn_cents']
  if (!attendues.some((k) => k in o)) return null
  const coupures = Array.isArray(o['denominations_cents'])
    ? o['denominations_cents'].map(entier).filter((n): n is number => n !== null && n > 0)
    : []
  return {
    moisDebut: entier(o['season_start_month']),
    jourDebut: entier(o['season_start_day']),
    orphelineAuto: o['orphan_auto_create'] === true,
    coupures: [...new Set(coupures)].sort((a, b) => b - a),
    seuilSignalement: entier(o['discrepancy_warn_cents']),
    seuilAlerte: entier(o['discrepancy_alert_cents']),
    saisonCourante: saisonDe(o['current_season']),
    // Défauts = ceux du serveur, au cas où une ancienne API ne les servirait pas.
    suiviDepuis: texte(o['closing_tracking_from']) ?? '2026-09-01',
    graceZapHeures: entier(o['zap_grace_hours']) ?? 48,
    delaiClotureHeures: entier(o['closing_due_hours']) ?? 72,
    recapMardi: o['weekly_review_enabled'] !== false,
    seuilRemplacement: entier(o['validation_replace_threshold_cents']) ?? 5000,
    // Défauts = ceux du serveur (migration 1759000100000) : entrées allumées, chiffre ÉTEINT.
    assoEntrees: o['associations_entries_enabled'] !== false,
    assoDansLeChiffre: o['associations_revenue_enabled'] === true,
    assoPartDefaut: entier(o['associations_share_default_cents']) ?? 200,
    assoFrais: o['associations_stripe_fees_enabled'] === true,
    assoFraisBp: entier(o['associations_stripe_fee_bp']) ?? 150,
    assoFraisFixe: entier(o['associations_stripe_fee_fixed_cents']) ?? 25,
  }
}

/** Les bornes de l'exercice en cours, telles que le serveur les a calculées. */
function saisonDe(v: unknown): { debut: string; fin: string } | null {
  if (!estObjet(v)) return null
  const debut = texte(v['start'])
  const fin = texte(v['end'])
  return debut !== null && fin !== null ? { debut, fin } : null
}

function joursDuMois(annee: number, mois: number): number {
  return new Date(Date.UTC(annee, mois, 0)).getUTCDate()
}

const FMT_JOUR_MOIS = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', timeZone: 'UTC' })

/**
 * « du 1 septembre au 31 août ». La borne de fin se DÉDUIT du début (la veille
 * du prochain anniversaire) : elle n'est pas une seconde décision qu'on
 * pourrait régler de travers. Un exercice au 31 d'un mois de 30 jours se rabat
 * sur le dernier jour réel, comme en base.
 */
function libelleExercice(mois: number, jour: number): string {
  const annee = new Date().getUTCFullYear()
  const debut = new Date(Date.UTC(annee, mois - 1, Math.min(jour, joursDuMois(annee, mois))))
  const prochain = new Date(Date.UTC(annee + 1, mois - 1, Math.min(jour, joursDuMois(annee + 1, mois))))
  prochain.setUTCDate(prochain.getUTCDate() - 1)
  return `du ${FMT_JOUR_MOIS.format(debut)} au ${FMT_JOUR_MOIS.format(prochain)}`
}

/* ═══════════════════════ état des sources (jamais un jeton) ═════════════ */

interface EtatSources { total: number; parStatut: { label: string; nombre: number }[] }

const STATUT_SOURCE: Record<string, string> = {
  RECEIVED: 'Reçu', PARSING: 'Lecture en cours', PARSED: 'Lu',
  NEEDS_REVIEW: 'À vérifier', FAILED: 'Lecture impossible', DUPLICATE: 'Doublon',
}

function lireSources(v: unknown): EtatSources | null {
  const brut = estObjet(v) ? (v['documents'] ?? v['sources'] ?? v['rows']) : v
  if (!Array.isArray(brut)) return null
  const compte = new Map<string, number>()
  for (const it of brut) {
    if (!estObjet(it)) continue
    const s = texte(it['status']) ?? 'RECEIVED'
    compte.set(s, (compte.get(s) ?? 0) + 1)
  }
  return {
    total: brut.length,
    parStatut: [...compte.entries()].map(([code, nombre]) => ({ label: STATUT_SOURCE[code] ?? code, nombre })),
  }
}

/* ═══════════════════════════════ la page ═══════════════════════════════ */

export function FinanceRulesPage() {
  const { permissions, loading } = usePermissions()
  const reglages = useRessource('/api/v1/finance/settings', lireReglages)
  const peutEcrire = !loading && permissions.includes('finance.settings.manage')

  return (
    <main className="lc-page lc-page--management lc-stagger">
      <SmartBack />

      <header className="fr-hero">
        <div>
          <h1 className="lc-title"><Scale size={26} aria-hidden="true" /> Règles & automatisations</h1>
          <p className="lc-subtitle">
            L’exercice comptable, les coupures comptées, les seuils à partir desquels
            un écart alerte, et la conduite à tenir devant une nuit sans soirée déclarée.
          </p>
        </div>
      </header>

      <EtatBloc etat={reglages.etat} quoi="les règles de la comptabilité" />

      {reglages.etat.phase === 'ok' ? (
        <FormulaireRegles
          initial={reglages.etat.data}
          peutEcrire={peutEcrire}
          peutVoirSoirees={!loading && permissions.includes('soiree.read')}
          onEnregistre={reglages.recharger}
        />
      ) : null}

      <PanneauSources autorise={!loading && permissions.includes('finance.source.read')} />
    </main>
  )
}

function FormulaireRegles(props: { initial: Reglages; peutEcrire: boolean; peutVoirSoirees: boolean; onEnregistre: () => void }) {
  const [mois, setMois] = useState<number | null>(props.initial.moisDebut)
  const [jour, setJour] = useState<number | null>(props.initial.jourDebut)
  const [orpheline, setOrpheline] = useState(props.initial.orphelineAuto)
  const [coupures, setCoupures] = useState<number[]>(props.initial.coupures)
  const [signalement, setSignalement] = useState(
    props.initial.seuilSignalement === null ? '' : String(props.initial.seuilSignalement / 100).replace('.', ','),
  )
  const [alerte, setAlerte] = useState(
    props.initial.seuilAlerte === null ? '' : String(props.initial.seuilAlerte / 100).replace('.', ','),
  )
  const [suiviDepuis, setSuiviDepuis] = useState(props.initial.suiviDepuis)
  const [grace, setGrace] = useState(String(props.initial.graceZapHeures))
  const [delai, setDelai] = useState(String(props.initial.delaiClotureHeures))
  const [recap, setRecap] = useState(props.initial.recapMardi)
  const [remplacement, setRemplacement] = useState(String(props.initial.seuilRemplacement / 100).replace('.', ','))
  const centsRemplacement = parseMoney(remplacement)
  const [assoEntrees, setAssoEntrees] = useState(props.initial.assoEntrees)
  const [assoChiffre, setAssoChiffre] = useState(props.initial.assoDansLeChiffre)
  const [assoPart, setAssoPart] = useState(euros(props.initial.assoPartDefaut))
  const [assoFrais, setAssoFrais] = useState(props.initial.assoFrais)
  const [assoTaux, setAssoTaux] = useState(pourcent(props.initial.assoFraisBp))
  const [assoFixe, setAssoFixe] = useState(euros(props.initial.assoFraisFixe))
  const centsPart = parseMoney(assoPart)
  // Les champs des frais ne sont affichés que l'estimation allumée : éteinte,
  // ils reviennent à la valeur enregistrée — jamais une erreur sur un champ
  // qu'on ne voit plus, qui bloquait la Save Bar (revue du 16/10).
  const bpFrais = assoFrais ? lirePourcentBp(assoTaux) : props.initial.assoFraisBp
  const centsFixe = assoFrais ? parseMoney(assoFixe) : props.initial.assoFraisFixe
  const basculerFrais = (v: boolean) => {
    setAssoFrais(v)
    if (!v) { setAssoTaux(pourcent(props.initial.assoFraisBp)); setAssoFixe(euros(props.initial.assoFraisFixe)) }
  }
  const graceH = /^\d{1,3}$/.test(grace.trim()) ? Number(grace.trim()) : null
  const delaiH = /^\d{1,3}$/.test(delai.trim()) ? Number(delai.trim()) : null

  const centsSignalement = parseMoney(signalement)
  const centsAlerte = parseMoney(alerte)
  const configuree = mois !== null && jour !== null
  // Tant que la saisie n'a pas bougé, on affiche les bornes CALCULÉES PAR LE
  // SERVEUR ; dès qu'elle bouge, on annonce ce qui sera appliqué, sans
  // prétendre que c'est déjà en base.
  const dirtySaison = mois !== props.initial.moisDebut || jour !== props.initial.jourDebut

  const dirty = mois !== props.initial.moisDebut || jour !== props.initial.jourDebut
    || orpheline !== props.initial.orphelineAuto
    || coupures.join(',') !== props.initial.coupures.join(',')
    || centsSignalement !== props.initial.seuilSignalement
    || centsAlerte !== props.initial.seuilAlerte
    || suiviDepuis !== props.initial.suiviDepuis
    || graceH !== props.initial.graceZapHeures
    || delaiH !== props.initial.delaiClotureHeures
    || recap !== props.initial.recapMardi
    || centsRemplacement !== props.initial.seuilRemplacement
    || assoEntrees !== props.initial.assoEntrees || assoChiffre !== props.initial.assoDansLeChiffre
    || centsPart !== props.initial.assoPartDefaut || assoFrais !== props.initial.assoFrais
    || bpFrais !== props.initial.assoFraisBp || centsFixe !== props.initial.assoFraisFixe

  const invalide = useMemo(() => {
    if (centsSignalement === null || centsSignalement < 0) return 'Le seuil de signalement doit être un montant positif.'
    if (centsAlerte === null || centsAlerte < 0) return 'Le seuil d’alerte doit être un montant positif.'
    if (centsAlerte < centsSignalement) return 'Le seuil d’alerte doit être supérieur ou égal au seuil de signalement : sinon toute alerte serait déjà un simple signalement.'
    if (coupures.length === 0) return 'Au moins une coupure doit rester proposée au comptage.'
    if ((mois === null) !== (jour === null)) return 'Un mois sans jour (ou l’inverse) ne définit rien : les deux valent ensemble, ou aucun ne vaut.'
    if (!/^\d{4}-\d{2}-\d{2}$/.test(suiviDepuis)) return 'La première nuit suivie s’écrit AAAA-MM-JJ, par exemple 2026-09-01.'
    if (graceH === null || graceH > 336) return 'Le délai d’attente du rapport ZAP est un nombre d’heures, de 0 à 336.'
    if (delaiH === null || delaiH > 720) return 'Le délai de clôture est un nombre d’heures, de 0 à 720.'
    if (centsRemplacement === null || centsRemplacement < 0) return 'La baisse tolérée avant de remplacer une validation doit être un montant positif.'
    if (centsPart === null || centsPart < 0 || centsPart > 100_000) return 'La part de l’association par billet est un montant de 0 à 1 000 €.'
    if (bpFrais === null) return 'Le taux des frais Stripe estimés est un pourcentage de 0 à 10, par exemple 1,5.'
    if (centsFixe === null || centsFixe < 0 || centsFixe > 1_000) return 'La part fixe des frais Stripe estimés est un montant de 0 à 10 € par commande.'
    return null
  }, [centsSignalement, centsAlerte, coupures, mois, jour, suiviDepuis, graceH, delaiH, centsRemplacement, centsPart, bpFrais, centsFixe])

  useSaveBar({
    dirty: props.peutEcrire && dirty,
    invalid: invalide,
    successMessage: 'Règles enregistrées.',
    onSave: async () => {
      const r = await adminMutate<{ error?: string; message?: string }>(
        'PUT', '/api/v1/finance/settings',
        {
          season_start_month: mois,
          season_start_day: jour,
          orphan_auto_create: orpheline,
          denominations_cents: [...coupures].sort((a, b) => b - a),
          discrepancy_warn_cents: centsSignalement,
          discrepancy_alert_cents: centsAlerte,
          closing_tracking_from: suiviDepuis,
          zap_grace_hours: graceH,
          closing_due_hours: delaiH,
          weekly_review_enabled: recap,
          validation_replace_threshold_cents: centsRemplacement,
          associations_entries_enabled: assoEntrees,
          associations_revenue_enabled: assoChiffre,
          associations_share_default_cents: centsPart,
          associations_stripe_fees_enabled: assoFrais,
          associations_stripe_fee_bp: bpFrais,
          associations_stripe_fee_fixed_cents: centsFixe,
        },
      )
      if (r.status >= 200 && r.status < 300) { props.onEnregistre(); return true }
      if (r.data.error === 'NOT_FOUND') return 'La route d’écriture des règles n’est pas encore servie par l’API : rien n’a été enregistré.'
      return r.data.message ?? 'Le serveur a refusé ces règles.'
    },
    onCancel: () => {
      setMois(props.initial.moisDebut); setJour(props.initial.jourDebut)
      setOrpheline(props.initial.orphelineAuto); setCoupures(props.initial.coupures)
      setSignalement(props.initial.seuilSignalement === null ? '' : String(props.initial.seuilSignalement / 100).replace('.', ','))
      setAlerte(props.initial.seuilAlerte === null ? '' : String(props.initial.seuilAlerte / 100).replace('.', ','))
      setSuiviDepuis(props.initial.suiviDepuis); setGrace(String(props.initial.graceZapHeures))
      setDelai(String(props.initial.delaiClotureHeures)); setRecap(props.initial.recapMardi)
      setRemplacement(String(props.initial.seuilRemplacement / 100).replace('.', ','))
      setAssoEntrees(props.initial.assoEntrees); setAssoChiffre(props.initial.assoDansLeChiffre)
      setAssoPart(euros(props.initial.assoPartDefaut)); setAssoFrais(props.initial.assoFrais)
      setAssoTaux(pourcent(props.initial.assoFraisBp)); setAssoFixe(euros(props.initial.assoFraisFixe))
    },
  })

  const basculerCoupure = (c: number) => {
    setCoupures((x) => x.includes(c) ? x.filter((v) => v !== c) : [...x, c].sort((a, b) => b - a))
  }

  return (
    <>
      {!props.peutEcrire ? (
        <div className="fr-note">
          <Info size={16} aria-hidden="true" />
          <span>
            <strong>Lecture seule.</strong> Vos droits permettent de consulter ces règles,
            pas de les modifier. Une modification exige une ré-authentification forte
            et laisse un nom, une date et la valeur d’avant.
          </span>
        </div>
      ) : null}

      {/* ── Exercice comptable ── */}
      <section className="lc-panel">
        <header className="lc-panel__head">
          <CalendarCheck size={18} aria-hidden="true" />
          <div>
            <div className="fr-editor__title">Exercice comptable</div>
            <div className="fr-editor__sub">Le début de saison. Il n’a aucune valeur par défaut.</div>
          </div>
          <span className="lc-panel__spacer" />
          {configuree
            ? <span className="lc-pill lc-pill--ok">Configuré</span>
            : <span className="lc-pill lc-pill--warning">Saison à configurer</span>}
        </header>
        <div className="lc-panel__body">
          {!configuree ? (
            <div className="fr-note fr-note--warn">
              <Info size={16} aria-hidden="true" />
              <span>
                <strong>Saison à configurer.</strong> Tant que ce réglage est vide, les
                écrans de CA affichent « Saison à configurer » plutôt qu’un cumul.
                Gestion ne choisit pas de mois à votre place : un exercice comptable
                est une décision de l’établissement, pas une valeur plausible.
              </span>
            </div>
          ) : null}

          <div className="fr-fields fr-fields--pair">
            <Field label="Mois d’ouverture">
              {props.peutEcrire ? (
                <Select
                  value={mois === null ? '' : String(mois)}
                  options={[{ value: '', label: 'Non configuré' }, ...MOIS.map((m, i) => ({ value: String(i + 1), label: m }))]}
                  onChange={(v) => { setMois(v === '' ? null : Number(v)); if (v === '') setJour(null); else if (jour === null) setJour(1) }}
                  ariaLabel="Mois d’ouverture de l’exercice"
                />
              ) : (
                <span className="fr-fact__value">{mois === null ? '—' : MOIS[mois - 1]}</span>
              )}
            </Field>
            <Field label="Jour d’ouverture">
              {props.peutEcrire ? (
                <Select
                  value={jour === null ? '' : String(jour)}
                  options={[{ value: '', label: 'Non configuré' }, ...Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))]}
                  onChange={(v) => setJour(v === '' ? null : Number(v))}
                  ariaLabel="Jour d’ouverture de l’exercice"
                />
              ) : (
                <span className="fr-fact__value">{jour === null ? '—' : jour}</span>
              )}
            </Field>
          </div>

          <p className="fr-mini">
            {!configuree
              ? 'Renseignez le mois ET le jour : l’un sans l’autre ne définit rien.'
              : props.initial.saisonCourante !== null && !dirtySaison
                ? `Exercice en cours, tel que le serveur le calcule : du ${libelleJour(props.initial.saisonCourante.debut)} au ${libelleJour(props.initial.saisonCourante.fin)}. La borne de fin se déduit du début — ce n’est pas un second réglage à tenir à jour.`
                : `L’exercice courra ${libelleExercice(mois, jour)}. Les bornes exactes seront recalculées par le serveur à l’enregistrement.`}
          </p>
        </div>
      </section>

      {/* ── Coupures ── */}
      <section className="lc-panel">
        <header className="lc-panel__head">
          <Coins size={18} aria-hidden="true" />
          <div>
            <div className="fr-editor__title">Coupures comptées</div>
            <div className="fr-editor__sub">La grille de comptage des espèces, de la plus grosse à la plus petite.</div>
          </div>
        </header>
        <div className="lc-panel__body">
          <div className="fr-chips">
            {COUPURES.map((c) => (
              <button
                key={c} type="button"
                className={`fr-chip${coupures.includes(c) ? ' fr-chip--on' : ''}`}
                aria-pressed={coupures.includes(c)}
                disabled={!props.peutEcrire}
                onClick={() => basculerCoupure(c)}
              >
                {formatMoney(c)}
              </button>
            ))}
          </div>
          <p className="fr-mini">
            Le billet de 500 € n’est pas proposé : son émission a cessé en 2019, et une
            ligne de comptage toujours vide est une ligne qu’on finit par remplir sans
            regarder. {coupures.length} coupure{coupures.length > 1 ? 's' : ''} active
            {coupures.length > 1 ? 's' : ''}.
          </p>
        </div>
      </section>

      {/* ── Seuils d'écart ── */}
      <section className="lc-panel">
        <header className="lc-panel__head">
          <Scale size={18} aria-hidden="true" />
          <div>
            <div className="fr-editor__title">Seuils d’écart</div>
            <div className="fr-editor__sub">À partir de quel montant un écart se signale, puis alerte.</div>
          </div>
        </header>
        <div className="lc-panel__body">
          <div className="fr-fields fr-fields--pair">
            <Field
              label="Signalement à partir de"
              helper={centsSignalement === null ? 'Un montant en euros. Exemple : 10' : `Signalé au-delà de ${formatMoney(centsSignalement)}.`}
            >
              {props.peutEcrire
                ? <TextInput value={signalement} onChange={setSignalement} placeholder="10" ariaLabel="Seuil de signalement" />
                : <span className="fr-fact__value">{formatMoney(props.initial.seuilSignalement)}</span>}
            </Field>
            <Field
              label="Alerte à partir de"
              helper={centsAlerte === null ? 'Un montant en euros. Exemple : 50' : `Alerte au-delà de ${formatMoney(centsAlerte)}.`}
            >
              {props.peutEcrire
                ? <TextInput value={alerte} onChange={setAlerte} placeholder="50" ariaLabel="Seuil d’alerte" />
                : <span className="fr-fact__value">{formatMoney(props.initial.seuilAlerte)}</span>}
            </Field>
          </div>
          <p className="fr-mini">
            Ces seuils changent ce que l’œil voit, jamais ce qui est enregistré : un
            écart sous le seuil reste un écart, écrit et consultable.
          </p>
        </div>
      </section>

      {/* ── Finaliser une nuit déjà validée (16/10/2026) ── */}
      <section className="lc-panel">
        <header className="lc-panel__head">
          <ShieldCheck size={18} aria-hidden="true" />
          <div>
            <div className="fr-editor__title">Finaliser une nuit déjà validée</div>
            <div className="fr-editor__sub">Quand une clôture ferait baisser le chiffre d’une nuit validée sans clôture détaillée.</div>
          </div>
        </header>
        <div className="lc-panel__body">
          <Field
            label="Motif exigé au-delà d’une baisse de"
            helper={centsRemplacement === null
              ? 'Un montant en euros. Exemple : 50'
              : centsRemplacement === 0
                ? 'Toute baisse, même d’un centime, demandera un motif.'
                : `Une baisse de plus de ${formatMoney(centsRemplacement)} demandera un motif avant de finaliser.`}
          >
            {props.peutEcrire
              ? <TextInput value={remplacement} onChange={setRemplacement} placeholder="50" ariaLabel="Baisse tolérée avant de remplacer une validation" />
              : <span className="fr-fact__value">{formatMoney(props.initial.seuilRemplacement)}</span>}
          </Field>
          <p className="fr-mini">
            Une clôture finalisée remplace la validation de sa nuit. Si la nuit a été validée plus haut
            (par exemple une nuit reprise de l’historique avec de la billetterie d’associations que la
            clôture ne compte pas), finaliser ferait disparaître cet argent. Au-delà de ce montant, la
            finalisation s’arrête et demande un motif, qui reste affiché sur la clôture et sur la fiche de la nuit.
          </p>
        </div>
      </section>

      {/* ── La billetterie des associations (Pretix), 16/10/2026 ──
          Décisions de l'exploitant : entrées = billets VENDUS (la DAF) ; le
          chiffre, « ça dépend de l'asso », ÉTEINT par défaut. Le nom du
          réglage est le sien. */}
      <section className="lc-panel">
        <header className="lc-panel__head">
          <Ticket size={18} aria-hidden="true" />
          <div>
            <div className="fr-editor__title">Billetterie des associations (Pretix)</div>
            <div className="fr-editor__sub">Les soirées des BDE vendues sur Pretix : leurs entrées, et ce que la nuit en garde.</div>
          </div>
          <span className="lc-panel__spacer" />
          {assoChiffre
            ? <span className="lc-pill lc-pill--ok">Comptée dans le chiffre</span>
            : <span className="lc-pill lc-pill--idle">Hors du chiffre</span>}
        </header>
        <div className="lc-panel__body">
          <SwitchRow
            title="Compter les billets vendus dans les entrées de la nuit"
            desc="Comme la DAF : les billets vendus, pas seulement scannés (979 le 09/09, pour 894 scannés). Ils s’ajoutent à ceux de Shotgun ; les scannés restent un contrôle. Le direct des Soirées, lui, montre toujours les scannés."
            checked={assoEntrees}
            onChange={setAssoEntrees}
            disabled={!props.peutEcrire}
          />
          <SwitchRow
            title="Billetterie associations dans le CA"
            desc="Éteint : ce que les acheteurs ont payé sur Pretix n’est qu’un repère, rien n’entre au chiffre de la nuit. Allumé, TOUT DE SUITE : une ligne à part, le prix des billets vendus moins la part de chaque association, entre au chiffre des nuits pas encore finalisées (fiche de nuit, accueil, proposition de « Valider sans clôture »), puis la finalisation l’arrête. Une nuit déjà finalisée ou déjà validée garde son chiffre."
            checked={assoChiffre}
            onChange={setAssoChiffre}
            disabled={!props.peutEcrire}
          />
          <Field
            label="Part de l’association par billet vendu, par défaut"
            helper={centsPart === null
              ? 'Un montant en euros par billet. Exemple : 2'
              : `${formatMoney(centsPart)} par billet, jamais plus que le prix du billet. Le 09/09, la DAF a compté chaque billet à son prix moins 2 € (8 → 6, 12 → 10, 17 → 15 €). Chaque événement peut avoir la sienne.`}
          >
            {props.peutEcrire
              ? <TextInput value={assoPart} onChange={setAssoPart} placeholder="2" ariaLabel="Part de l’association par billet, par défaut" />
              : <span className="fr-fact__value">{formatMoney(props.initial.assoPartDefaut)}</span>}
          </Field>
          <SwitchRow
            title="Déduire une estimation des frais Stripe"
            desc="Pretix ne connaît pas les frais réels de Stripe. Allumé, Gestion retire une estimation par commande payée, et le dit partout où le montant apparaît."
            checked={assoFrais}
            onChange={basculerFrais}
            disabled={!props.peutEcrire}
          />
          {assoFrais ? (
            <div className="fr-fields fr-fields--pair">
              <Field label="Taux estimé (%)" helper={bpFrais === null ? 'Un pourcentage de 0 à 10. Exemple : 1,5' : `${pourcent(bpFrais)} % du montant de chaque commande payée.`}>
                <TextInput value={assoTaux} onChange={setAssoTaux} placeholder="1,5" disabled={!props.peutEcrire} ariaLabel="Taux estimé des frais Stripe" />
              </Field>
              <Field label="Part fixe par commande" helper={centsFixe === null ? 'Un montant en euros. Exemple : 0,25' : `${formatMoney(centsFixe)} par commande payée.`}>
                <TextInput value={assoFixe} onChange={setAssoFixe} placeholder="0,25" disabled={!props.peutEcrire} ariaLabel="Part fixe des frais Stripe estimés" />
              </Field>
            </div>
          ) : null}
          <p className="fr-mini">
            Pourquoi un montant par billet plutôt qu’un pourcentage : c’est ce que la DAF applique, et un pourcentage
            ne retrouve pas les 8 021 € du 09/09. Chaque événement peut avoir sa part, ci-dessous
            {props.peutVoirSoirees ? <> ou à côté de sa nuit dans <Link href="/settings/soirees/billetteries">Réglages Soirées › Billetteries</Link></> : null}.
          </p>
          <PartsParEvenement peutRegler={props.peutEcrire} />
        </div>
      </section>

      {/* ── Les nuits : ce que le calendrier appelle « à faire » ── */}
      <section className="lc-panel">
        <header className="lc-panel__head">
          <CalendarDays size={18} aria-hidden="true" />
          <div>
            <div className="fr-editor__title">Suivi des nuits</div>
            <div className="fr-editor__sub">Quand une nuit devient « à faire », et quand elle passe au rouge.</div>
          </div>
        </header>
        <div className="lc-panel__body">
          <Field label="Première nuit suivie" helper="Avant elle, aucune nuit n’est comptée « à faire » dans le calendrier ni dans le récapitulatif.">
            <TextInput value={suiviDepuis} onChange={setSuiviDepuis} placeholder="2026-09-01" maxLength={10} disabled={!props.peutEcrire} ariaLabel="Première nuit suivie" />
          </Field>
          <Field label="Attente du rapport ZAP (heures après la fin de nuit)" helper="Passé ce délai, une soirée sans rapport ZAP est signalée en rouge. La nuit se termine le lendemain à 8 h.">
            <TextInput value={grace} onChange={setGrace} placeholder="48" maxLength={3} disabled={!props.peutEcrire} ariaLabel="Attente du rapport ZAP en heures" />
          </Field>
          <Field label="Délai de clôture (heures après la fin de nuit)" helper="Passé ce délai, une nuit non clôturée passe de l’orange au rouge.">
            <TextInput value={delai} onChange={setDelai} placeholder="72" maxLength={3} disabled={!props.peutEcrire} ariaLabel="Délai de clôture en heures" />
          </Field>
          <SwitchRow
            title="Récapitulatif du mardi"
            desc="Le mardi matin, la liste des nuits de la semaine écoulée qui attendent : soirée sans rapport ZAP, ou clôture non faite. Dans Gestion et par e-mail, aux administrateurs de la Comptabilité — l’e-mail suit vos préférences de notification (catégorie « Attention »)."
            checked={recap}
            onChange={setRecap}
            disabled={!props.peutEcrire}
          />
        </div>
      </section>

      {/* ── Nuit sans soirée déclarée ── */}
      <section className="lc-panel">
        <header className="lc-panel__head">
          <Info size={18} aria-hidden="true" />
          <div>
            <div className="fr-editor__title">Nuit sans soirée déclarée</div>
            <div className="fr-editor__sub">Ce que Gestion fait quand de l’argent existe sans événement connu.</div>
          </div>
        </header>
        <div className="lc-panel__body">
          <SwitchRow
            title="Signaler les nuits sans soirée déclarée"
            desc="Allumé, une nuit qui porte de l’argent sans soirée connue est signalée pour être déclarée à la main. Gestion ne CRÉE jamais la soirée : une nuit sans soirée n’a ni nom, ni heure d’ouverture, ni billet — il n’y a rien à partir de quoi la reconstituer sans l’inventer. Éteint, la clôture s’ouvre quand même et la nuit reste sans soirée, ce qui est la vérité."
            checked={orpheline}
            onChange={setOrpheline}
            disabled={!props.peutEcrire}
          />
          <p className="fr-note">
            L’intitulé de ce réglage promettait autrefois une création automatique qui n’a jamais
            existé — aucun code ne le lisait. Il est désormais lu, et il fait ce qu’il dit.
          </p>
        </div>
      </section>
    </>
  )
}

/* ═══════════════════ la part de chaque événement d'association ════════════════ */

/** Au-delà, la liste se replie : la saison en cours d'abord, le reste sur demande. */
const PARTS_VISIBLES = 8

/**
 * LA PART PAR ÉVÉNEMENT, DANS LES RÈGLES (revue du 16/10/2026). Le rôle
 * compta a le droit de la régler (`finance.settings.manage`) mais n'ouvre
 * pas les Réglages Soirées : sans cette liste, seul un super-administrateur
 * pouvait appliquer « ça dépend de l'asso ». Un geste par événement, hors de
 * la Save Bar (bouton explicite, ré-authentification, tracé).
 */
function PartsParEvenement({ peutRegler }: { peutRegler: boolean }) {
  const [etat, setEtat] = useState<{ parts: PartsAssociations; events: EvenementPart[] } | 'erreur' | null>(null)
  const [tout, setTout] = useState(false)
  const charger = () => {
    void apiGet<Parameters<typeof lireParts>[0]>('/api/v1/finance/associations/shares')
      .then((j) => setEtat({ parts: lireParts(j), events: j.events }))
      .catch(() => setEtat('erreur'))
  }
  useEffect(() => { charger() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (etat === null) return <p className="fr-mini">Lecture des événements d’associations en cours.</p>
  if (etat === 'erreur') return <p className="fr-mini">Les événements d’associations n’ont pas pu être lus : la part de chacun ne s’affiche pas ici pour l’instant.</p>
  if (etat.events.length === 0) {
    return <p className="fr-mini">Aucun événement d’association pour l’instant : ils apparaissent ici dès que la lecture de Pretix est allumée (Administration › Intégrations).</p>
  }
  const visibles = tout ? etat.events : etat.events.slice(0, PARTS_VISIBLES)
  return (
    <div className="fba-parts" aria-label="Part de l’association, événement par événement">
      {visibles.map((e) => (
        <div key={e.event_id} className="fba-parts__item">
          <span className="fba-parts__nom">{e.name}</span>
          <span className="fba__sous">Nuit du {libelleJourLong(e.night).toLowerCase()}</span>
          <PartAssociation evenement={{ id: e.event_id, name: e.name }} parts={etat.parts} peutRegler={peutRegler} onFait={charger} />
        </div>
      ))}
      {etat.events.length > PARTS_VISIBLES ? (
        <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => setTout(!tout)}>
          {tout ? 'Montrer les plus récents seulement' : `Montrer les ${etat.events.length} événements`}
        </button>
      ) : null}
    </div>
  )
}

/* ═══════════════════ sources : un état, jamais un jeton ════════════════ */

function PanneauSources({ autorise }: { autorise: boolean }) {
  const depuis = useMemo(() => {
    const d = new Date()
    d.setUTCDate(d.getUTCDate() - 30)
    return d.toISOString().slice(0, 10)
  }, [])
  const jusqua = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const sources = useRessource(
    autorise ? `/api/v1/finance/sources?from=${depuis}&to=${jusqua}` : null,
    lireSources,
  )
  const d = sources.etat.phase === 'ok' ? sources.etat.data : null

  return (
    <section className="lc-panel">
      <header className="lc-panel__head">
        <ShieldCheck size={18} aria-hidden="true" />
        <div>
          <div className="fr-editor__title">Sources & secrets</div>
          <div className="fr-editor__sub">Ce que cet écran sait, et ce qu’il ne détient pas.</div>
        </div>
      </header>
      <div className="lc-panel__body">
        <div className="fr-note">
          <Info size={16} aria-hidden="true" />
          <span>
            <strong>Aucun jeton ne s’affiche ni ne se saisit ici.</strong> Identifiants,
            clés et connexions fournisseur vivent dans{' '}
            <Link className="lc-link" href="/settings/integrations">Administration → Intégrations</Link>,
            et nulle part ailleurs. Cet écran peut dire qu’une source va bien ; il ne
            donne jamais de quoi s’y connecter.
          </span>
        </div>

        {!autorise ? (
          <p className="fr-mini">
            L’état des documents sources n’est pas lisible avec vos droits. Ce n’est pas
            un affichage masqué : la donnée n’a pas été demandée.
          </p>
        ) : d === null ? (
          <p className="fr-mini">
            L’état des documents sources n’est pas disponible ici pour l’instant. Il se
            lit sur l’écran Clôtures & caisses, où les documents reçus vivent.
          </p>
        ) : d.total === 0 ? (
          <p className="fr-mini">Aucun document source reçu sur les trente derniers jours.</p>
        ) : (
          <div className="fr-facts">
            <div className="fr-fact">
              <span className="fr-fact__label">Documents reçus (30 jours)</span>
              <span className="fr-fact__value">{d.total}</span>
            </div>
            {d.parStatut.map((s) => (
              <div key={s.label} className="fr-fact">
                <span className="fr-fact__label">{s.label}</span>
                <span className="fr-fact__value">{s.nombre}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
